import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  Copy,
  ExternalLink,
  FileText,
  Files,
  History,
  Inbox,
  Mail,
  MoreVertical,
  Pencil,
  RefreshCw,
  User,
} from "lucide-react";

import { callApi } from "../api/api";
import DocumentTypeMultiSelect from "../components/DocumentTypeMultiSelect";
import InboxReviewThreadView from "../components/InboxReviewThreadView";
import LoadingOverlay from "../components/LoadingOverlay";
import Pagination from "../components/Pagination";
import RequestHistoryModal from "../components/RequestHistoryModal";
import ScrollableTable from "../components/ScrollableTable";
import { useReviewMode } from "../context/ReviewModeContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { formatAssignedTo } from "./inboxReviewScope";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";
import {
  mergeSelectedTypeCounts,
  shouldGroupWorkPoolByDocumentType,
} from "../utils/workPoolGrouping";

const PAGE_SIZE = 10;

function formatQueueCount(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function formatDate(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString();
}

function getFileNameFromPath(path) {
  if (!path) return "";
  const parts = String(path).split("/");
  return parts[parts.length - 1];
}

function previewKindFromPath(path) {
  const lower = String(path || "").toLowerCase().split("?")[0];
  if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/.test(lower)) {
    return "image";
  }
  if (/\.(tif|tiff|pdf)$/.test(lower)) {
    return "pdf";
  }
  if (lower.endsWith(".eml")) {
    return "html";
  }
  return "file";
}

function needsConvertedPreview(path) {
  const lower = String(path || "").toLowerCase().split("?")[0];
  return (
    lower.endsWith(".tif") ||
    lower.endsWith(".tiff") ||
    lower.endsWith(".eml") ||
    lower.endsWith(".heic") ||
    lower.endsWith(".heif")
  );
}

const PREVIEW_CACHE_LIMIT = 8;

function rememberPreview(cache, path, url, kind) {
  if (cache.has(path)) {
    cache.delete(path);
  }
  cache.set(path, { url, kind });
  while (cache.size > PREVIEW_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value;
    const oldest = cache.get(oldestKey);
    cache.delete(oldestKey);
    if (oldest?.url?.startsWith("blob:") && oldest.url !== url) {
      URL.revokeObjectURL(oldest.url);
    }
  }
}

function isVerifiedEmail(email) {
  return email?.verified_flag === true || email?.verified_flag === 1;
}

function isChain(thread) {
  return (thread?.emails?.length || 0) > 1;
}

function threadSummary(thread) {
  const visible = thread?.emails?.length || 0;
  const total = visible || thread.thread_count || 0;
  const pending = thread.pending_count ?? 0;
  const chained = visible > 1;
  const emailWord = total === 1 ? "email" : "emails";

  return {
    title: chained ? "Thread" : "Email",
    detail: chained
      ? (pending ? `${total} emails · ${pending} pending` : `${total} emails`)
      : `${total} ${emailWord}`,
    link: chained ? "View thread" : "View email",
  };
}

function latestThreadEmail(thread) {
  const emails = thread?.emails || [];
  return emails[emails.length - 1] || null;
}

function emailFiles(email) {
  const files = [];

  if (email?.email_storage_path) {
    files.push({
      name: getFileNameFromPath(email.email_storage_path),
      path: email.email_storage_path,
      type: "email",
    });
  }

  for (const attachment of email?.attachments || []) {
    if (!attachment?.name && !attachment?.path) {
      continue;
    }

    files.push({
      name: attachment.name,
      path: attachment.path,
      type: "attachment",
    });
  }

  return files;
}

function toReviewRow(email, conversationId) {
  return {
    ...email,
    conversation_id: conversationId || email?.conversation_id,
    attachments: emailFiles(email),
  };
}

function nextPendingEmail(thread, savedMsgId) {
  const emails = thread?.emails || [];
  const savedIndex = emails.findIndex((email) => email.msg_id === savedMsgId);
  const later = emails
    .slice(savedIndex + 1)
    .find((email) => !isVerifiedEmail(email));

  if (later) {
    return later;
  }

  return (
    emails.find(
      (email) => email.msg_id !== savedMsgId && !isVerifiedEmail(email),
    ) || null
  );
}

function StatusChip({ tone, label }) {
  let toneStyle = styles.chipNew;
  let dotStyle = styles.chipDotNew;

  if (tone === "progress" || tone === "in_progress") {
    toneStyle = styles.chipProgress;
    dotStyle = styles.chipDotProgress;
  } else if (tone === "completed" || tone === "verified") {
    toneStyle = styles.chipVerified;
    dotStyle = styles.chipDotVerified;
  } else if (tone === "review") {
    toneStyle = styles.chipReview;
    dotStyle = styles.chipDotReview;
  }

  return (
    <span style={{ ...styles.chip, ...toneStyle }}>
      <span style={{ ...styles.chipDot, ...dotStyle }} />
      {label}
    </span>
  );
}

function rowAccent(chained, opened) {
  if (chained) {
    return opened ? styles.accentThreadDark : styles.accentThreadLite;
  }
  return opened ? styles.accentSingleDark : styles.accentSingleLite;
}

function threadStatus(thread) {
  const chained = isChain(thread);
  const singleton = thread?.emails?.[0];
  const metaEmail = chained ? latestThreadEmail(thread) : singleton;

  const isClosed = Boolean(
    thread?.is_closed ||
    metaEmail?.is_closed ||
    thread?.last_action_type === "REQUEST_CLOSED" ||
    metaEmail?.last_action_type === "REQUEST_CLOSED" ||
    thread?.status === "Completed" ||
    thread?.status === "Closed" ||
    thread?.state === "Closed" ||
    metaEmail?.state === "Closed"
  );

  if (isClosed) {
    return { tone: "completed", label: "Completed" };
  }

  const assignedTo =
    thread?.ado_assigned_to ||
    metaEmail?.ado_assigned_to ||
    thread?.assigned_to ||
    null;
  const displayName = formatAssignedTo(assignedTo);
  const hasAssignee = Boolean(displayName && displayName !== "Unassigned");

  if (hasAssignee) {
    return { tone: "progress", label: "In Progress" };
  }

  return { tone: "new", label: "New" };
}

function TruncatedWithTooltip({ text, style, copyable = true }) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const [copied, setCopied] = useState(false);

  const hide = () => setTip(null);

  useEffect(() => {
    if (!tip) {
      return undefined;
    }
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [tip]);

  const show = () => {
    const el = ref.current;
    const full = String(text || "");
    if (!el || !full || full === "—") {
      return;
    }
    if (el.scrollWidth <= el.clientWidth + 1) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setTip({ text: full, left: rect.left, top: rect.top });
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!text || text === "—") return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        maxWidth: "100%",
        minWidth: 0,
        position: "relative",
      }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <span
        ref={ref}
        style={{
          ...style,
          userSelect: "text",
          cursor: "text",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {text}
      </span>
      {copyable && text && text !== "—" && (
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? "Copied!" : `Copy: ${text}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: copied ? "#dcfce7" : "#f1f5f9",
            color: copied ? "#16a34a" : "#64748b",
            border: copied ? "1px solid #86efac" : "1px solid #e2e8f0",
            borderRadius: 4,
            width: 20,
            height: 20,
            padding: 0,
            cursor: "pointer",
            flexShrink: 0,
            transition: "all 0.15s ease",
          }}
        >
          {copied ? <Check size={11} color="#16a34a" /> : <Copy size={11} />}
        </button>
      )}
      {tip
        ? createPortal(
            <div
              style={{
                ...styles.subjectTooltip,
                left: tip.left,
                top: tip.top,
                pointerEvents: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
              role="tooltip"
              onMouseEnter={() => setTip(tip)}
              onMouseLeave={hide}
            >
              <span style={{ userSelect: "text" }}>{tip.text}</span>
              {copyable && (
                <button
                  type="button"
                  onClick={handleCopy}
                  title="Copy"
                  style={{
                    background: copied ? "#16a34a" : "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 6px",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={11} />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={11} />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              )}
              <span style={styles.subjectTooltipArrow} aria-hidden="true" />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default function InboxReviewVersion2() {
  const { instance, accounts } = useMsal();
  const { reviewMode, setReviewMode } = useReviewMode();
  const isSmallScreen = useBreakpoint(480);
  const isNarrow = useBreakpoint(1024);

  const FILESYSTEM = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";

  const [activeTab, setActiveTab] = useState("pending");
  const [senderEmail, setSenderEmail] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedSenderEmail, setAppliedSenderEmail] = useState("");
  const [appliedDocumentTypes, setAppliedDocumentTypes] = useState([]);
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [recordTotal, setRecordTotal] = useState(0);
  const [individualTotal, setIndividualTotal] = useState(0);
  const [groupedTotal, setGroupedTotal] = useState(0);

  const [verifiedThreads, setVerifiedThreads] = useState([]);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [verifiedPage, setVerifiedPage] = useState(1);
  const [verifiedTotal, setVerifiedTotal] = useState(0);
  const [verifiedRecordTotal, setVerifiedRecordTotal] = useState(0);
  const [verifiedIndividualTotal, setVerifiedIndividualTotal] = useState(0);
  const [verifiedGroupedTotal, setVerifiedGroupedTotal] = useState(0);

  const [openThread, setOpenThread] = useState(null);
  const [selectedMsgId, setSelectedMsgId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [inlinePreviewUrl, setInlinePreviewUrl] = useState(null);
  const [inlinePreviewKind, setInlinePreviewKind] = useState("file");
  const [inlinePreviewError, setInlinePreviewError] = useState(null);
  const previewUrlRef = useRef(null);
  const previewAbortRef = useRef(null);
  const previewCacheRef = useRef(new Map());
  const previewPathRef = useRef(null);
  const [toast, setToast] = useState(null);
  const queueTopRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [typeCounts, setTypeCounts] = useState([]);
  const [groupPages, setGroupPages] = useState({});
  const [groupResults, setGroupResults] = useState({});
  const [historyModalItem, setHistoryModalItem] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const appliedDocumentTypesParam = appliedDocumentTypes.length
    ? appliedDocumentTypes.join(",")
    : undefined;

  const loadPendingThreads = useCallback(
    async (signal) => {
      const grouped = shouldGroupWorkPoolByDocumentType(appliedDocumentTypes);
      const threadParams = {
        sender_email: appliedSenderEmail || undefined,
        document_types: appliedDocumentTypesParam,
        from_date: appliedFromDate || undefined,
        to_date: appliedToDate || undefined,
      };
      try {
        setLoading(true);
        const [res, typeCountRes] = await Promise.all([
          callApi(
            instance,
            accounts,
            "GET",
            "/api/metadata/pending-threads",
            null,
            {
              signal,
              params: {
                page: grouped ? 1 : page,
                pageSize: grouped ? 1 : PAGE_SIZE,
                ...threadParams,
              },
            },
          ),
          grouped
            ? callApi(
                instance,
                accounts,
                "GET",
                "/api/metadata/pending-threads",
                null,
                {
                  signal,
                  params: {
                    counts_by_type: 1,
                    ...threadParams,
                  },
                },
              )
            : Promise.resolve(null),
        ]);

        if (signal?.aborted) return null;

        const data = grouped ? [] : res?.data?.data || [];
        const totalCount = res?.data?.total || 0;
        const records = res?.data?.recordTotal || totalCount;
        setThreads(data);
        setTotal(totalCount);
        setRecordTotal(records);
        setIndividualTotal(res?.data?.individualCount || 0);
        setGroupedTotal(res?.data?.groupedCount || 0);
        setTypeCounts(
          grouped
            ? mergeSelectedTypeCounts(
                appliedDocumentTypes,
                typeCountRes?.data?.data || [],
              )
            : [],
        );
        return { data, total: totalCount, grouped };
      } catch (err) {
        if (err?.message === "Request cancelled" || err?.name === "CanceledError") {
          return null;
        }
        console.error("Failed to load pending threads", err);
        setThreads([]);
        setTotal(0);
        setRecordTotal(0);
        setIndividualTotal(0);
        setGroupedTotal(0);
        setTypeCounts([]);
        return null;
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      page,
      appliedSenderEmail,
      appliedDocumentTypes,
      appliedDocumentTypesParam,
      appliedFromDate,
      appliedToDate,
    ],
  );

  const loadVerifiedThreads = useCallback(
    async (signal) => {
      const grouped = shouldGroupWorkPoolByDocumentType(appliedDocumentTypes);
      const threadParams = {
        sender_email: appliedSenderEmail || undefined,
        document_types: appliedDocumentTypesParam,
        from_date: appliedFromDate || undefined,
        to_date: appliedToDate || undefined,
      };
      try {
        setVerifiedLoading(true);
        const [res, typeCountRes] = await Promise.all([
          callApi(
            instance,
            accounts,
            "GET",
            "/api/metadata/verified-threads",
            null,
            {
              signal,
              params: {
                page: grouped ? 1 : verifiedPage,
                pageSize: grouped ? 1 : PAGE_SIZE,
                ...threadParams,
              },
            },
          ),
          grouped
            ? callApi(
                instance,
                accounts,
                "GET",
                "/api/metadata/verified-threads",
                null,
                {
                  signal,
                  params: {
                    counts_by_type: 1,
                    ...threadParams,
                  },
                },
              )
            : Promise.resolve(null),
        ]);

        if (signal?.aborted) return null;

        const data = grouped ? [] : res?.data?.data || [];
        const totalCount = res?.data?.total || 0;
        const records = res?.data?.recordTotal || totalCount;
        setVerifiedThreads(data);
        setVerifiedTotal(totalCount);
        setVerifiedRecordTotal(records);
        setVerifiedIndividualTotal(res?.data?.individualCount || 0);
        setVerifiedGroupedTotal(res?.data?.groupedCount || 0);
        setTypeCounts(
          grouped
            ? mergeSelectedTypeCounts(
                appliedDocumentTypes,
                typeCountRes?.data?.data || [],
              )
            : [],
        );
        return { data, total: totalCount, grouped };
      } catch (err) {
        if (err?.message === "Request cancelled" || err?.name === "CanceledError") {
          return null;
        }
        console.error("Failed to load verified threads", err);
        setVerifiedThreads([]);
        setVerifiedTotal(0);
        setVerifiedRecordTotal(0);
        setVerifiedIndividualTotal(0);
        setVerifiedGroupedTotal(0);
        setTypeCounts([]);
        return null;
      } finally {
        if (!signal?.aborted) {
          setVerifiedLoading(false);
        }
      }
    },
    [
      instance,
      accounts,
      verifiedPage,
      appliedSenderEmail,
      appliedDocumentTypes,
      appliedDocumentTypesParam,
      appliedFromDate,
      appliedToDate,
    ],
  );

  const loadDocumentTypeGroup = useCallback(
    async (type, nextPage = 1) => {
      const url =
        activeTab === "pending"
          ? "/api/metadata/pending-threads"
          : "/api/metadata/verified-threads";
      setGroupPages((current) => ({ ...current, [type]: nextPage }));
      setGroupResults((current) => ({
        ...current,
        [type]: {
          data: current[type]?.data || [],
          total: current[type]?.total || 0,
          loading: true,
        },
      }));
      try {
        const res = await callApi(instance, accounts, "GET", url, null, {
          params: {
            page: nextPage,
            pageSize: PAGE_SIZE,
            sender_email: appliedSenderEmail || undefined,
            document_types: type,
            from_date: appliedFromDate || undefined,
            to_date: appliedToDate || undefined,
          },
        });
        const payload = {
          data: res?.data?.data || [],
          total: res?.data?.total || 0,
          loading: false,
        };
        setGroupResults((current) => ({ ...current, [type]: payload }));
        return payload;
      } catch (err) {
        if (err?.message === "Request cancelled" || err?.name === "CanceledError") {
          return null;
        }
        console.error("Failed to load document type group", err);
        const payload = { data: [], total: 0, loading: false };
        setGroupResults((current) => ({ ...current, [type]: payload }));
        return payload;
      }
    },
    [
      accounts,
      activeTab,
      appliedFromDate,
      appliedSenderEmail,
      appliedToDate,
      instance,
    ],
  );

  useEffect(() => {
    if (!accounts.length) {
      return undefined;
    }

    const controller = new AbortController();

    (async () => {
      try {
        const res = await callApi(
          instance,
          accounts,
          "GET",
          "/api/admin/lookup/document_type",
          null,
          { signal: controller.signal },
        );
        const rows = res?.data?.data || [];
        const codes = [
          ...new Set(
            rows
              .map((row) => row.lookup_code || row.lookup_name)
              .map((code) => String(code || "").trim())
              .filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b));
        setDocumentTypeOptions(codes);
      } catch (err) {
        if (err?.message === "Request cancelled" || err?.name === "CanceledError") {
          return;
        }
        console.error("Failed to load document types", err);
        setDocumentTypeOptions([]);
      }
    })();

    return () => controller.abort();
  }, [accounts, instance]);

  useEffect(() => {
    if (!accounts.length || activeTab !== "pending") {
      return;
    }

    const controller = new AbortController();
    loadPendingThreads(controller.signal);
    return () => controller.abort();
  }, [accounts.length, activeTab, loadPendingThreads]);

  useEffect(() => {
    if (!accounts.length || activeTab !== "verified") {
      return;
    }

    const controller = new AbortController();
    loadVerifiedThreads(controller.signal);
    return () => controller.abort();
  }, [accounts.length, activeTab, loadVerifiedThreads]);

  useEffect(() => {
    const cache = previewCacheRef.current;
    return () => {
      previewAbortRef.current?.abort();
      cache.forEach((entry) => {
        if (entry?.url?.startsWith("blob:")) {
          URL.revokeObjectURL(entry.url);
        }
      });
      cache.clear();
    };
  }, []);

  useEffect(() => {
    setOpenThread(null);
    setSelectedMsgId(null);
  }, [
    activeTab,
    page,
    verifiedPage,
    appliedSenderEmail,
    appliedDocumentTypes,
    appliedFromDate,
    appliedToDate,
  ]);

  useEffect(() => {
    setExpandedGroups(new Set());
    setGroupPages({});
    setGroupResults({});
  }, [
    activeTab,
    appliedDocumentTypes,
    appliedFromDate,
    appliedSenderEmail,
    appliedToDate,
  ]);

  const toggleDocumentTypeGroup = (type) => {
    const isExpanded = expandedGroups.has(type);
    if (isExpanded) {
      setExpandedGroups((current) => {
        const next = new Set(current);
        next.delete(type);
        return next;
      });
      return;
    }

    setExpandedGroups((current) => {
      const next = new Set(current);
      next.add(type);
      return next;
    });
    if (!groupResults[type]) {
      loadDocumentTypeGroup(type, groupPages[type] || 1);
    }
  };

  const openConversationId = openThread?.conversation_id;

  useLayoutEffect(() => {
    if (!openConversationId) {
      return;
    }

    const scrollParent =
      queueTopRef.current?.closest(".app-shell__content") ||
      document.querySelector(".app-shell__content");

    if (scrollParent) {
      scrollParent.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    window.scrollTo({ top: 0, behavior: "auto" });
  }, [openConversationId]);

  const handleSearch = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showToast("From Date cannot be after To Date", "error");
      return;
    }

    setPage(1);
    setVerifiedPage(1);
    setAppliedSenderEmail(senderEmail.trim());
    setAppliedDocumentTypes([...documentTypes]);
    setAppliedFromDate(fromDate ? new Date(fromDate).toISOString() : "");
    setAppliedToDate(toDate ? new Date(toDate).toISOString() : "");
  };

  const handleClearFilters = () => {
    setSenderEmail("");
    setDocumentTypes([]);
    setFromDate("");
    setToDate("");
    setAppliedSenderEmail("");
    setAppliedDocumentTypes([]);
    setAppliedFromDate("");
    setAppliedToDate("");
    setPage(1);
    setVerifiedPage(1);
  };

  const closeReview = () => {
    setReviewMode(false);
    setSelectedRow(null);
  };

  const closeThreadView = () => {
    setOpenThread(null);
    setSelectedMsgId(null);
    closeReview();
  };

  const openThreadView = (thread, preferredMsgId, options = {}) => {
    const emails = thread?.emails || [];
    const preferred = emails.find((email) => email.msg_id === preferredMsgId);
    const firstPending = emails.find((email) => !isVerifiedEmail(email));
    const selected =
      preferred || firstPending || emails[emails.length - 1] || emails[0];

    setOpenThread(thread);
    setSelectedMsgId(selected?.msg_id || null);

    if (options.startReview && selected) {
      setSelectedRow(toReviewRow(selected, thread.conversation_id));
      setReviewMode(true);
      return;
    }

    setReviewMode(false);
    setSelectedRow(null);
  };

  const openReview = (email, conversationId) => {
    const threadId = conversationId || email?.conversation_id;
    setSelectedRow(toReviewRow(email, threadId));
    setSelectedMsgId(email?.msg_id || null);
    setReviewMode(true);
  };

  const selectThreadEmail = (email) => {
    setSelectedMsgId(email?.msg_id || null);
    if (reviewMode) {
      closeReview();
    }
  };

  const getPreviewTargetUrl = useCallback(
    async (path, signal) => {
      const cleaned = path?.trim();
      if (!cleaned) {
        throw new Error("Preview path missing");
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
      const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;

      if (needsConvertedPreview(filePath)) {
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });
        const response = await fetch(
          `${baseUrl}/api/adls/preview-blob` +
            `?filesystem=${encodeURIComponent(filesystem)}` +
            `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            signal,
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to preview file (${response.status})`);
        }

        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          throw new Error("Empty preview response");
        }

        return URL.createObjectURL(blob);
      }

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/adls/preview-url",
        null,
        {
          signal,
          params: { filesystem, path: filePath },
        },
      );

      if (!res?.data?.url) {
        throw new Error("Preview URL missing");
      }

      return res.data.url;
    },
    [FILESYSTEM, accounts, instance],
  );

  const handlePreview = useCallback(
    async (path) => {
      const cleaned = path?.trim();
      if (!cleaned) {
        return;
      }

      const kind = previewKindFromPath(cleaned);
      const cached = previewCacheRef.current.get(cleaned);
      if (cached?.url) {
        previewAbortRef.current?.abort();
        previewPathRef.current = cleaned;
        previewUrlRef.current = cached.url;
        setInlinePreviewError(null);
        setInlinePreviewKind(cached.kind || kind);
        setInlinePreviewUrl(cached.url);
        setPreviewLoading(false);
        return;
      }

      if (
        previewPathRef.current === cleaned &&
        previewAbortRef.current &&
        !previewAbortRef.current.signal.aborted
      ) {
        return;
      }

      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      previewPathRef.current = cleaned;

      try {
        setPreviewLoading(true);
        setInlinePreviewError(null);
        setInlinePreviewKind(kind);
        setInlinePreviewUrl(null);

        const url = await getPreviewTargetUrl(cleaned, controller.signal);
        if (controller.signal.aborted) {
          if (url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
          return;
        }

        rememberPreview(previewCacheRef.current, cleaned, url, kind);
        previewUrlRef.current = url;
        setInlinePreviewUrl(url);
      } catch (err) {
        if (err?.name === "AbortError" || err?.message === "Request cancelled") {
          return;
        }
        if (previewPathRef.current === cleaned) {
          previewPathRef.current = null;
        }
        console.error("Preview failed", err);
        setInlinePreviewError(err?.message || "Failed to preview document");
        showToast(err?.message || "Failed to preview document", "error");
      } finally {
        if (previewAbortRef.current === controller) {
          previewAbortRef.current = null;
        }
        if (!controller.signal.aborted) {
          setPreviewLoading(false);
        }
      }
    },
    [getPreviewTargetUrl, showToast],
  );

  const handleOpenAllAttachments = useCallback(
    async (files) => {
      const paths = (files || [])
        .map((file) => file?.path?.trim())
        .filter(Boolean);
      if (!paths.length) {
        return;
      }

      const tabs = paths.map(() => window.open("about:blank", "_blank"));
      const openedCount = tabs.filter(Boolean).length;
      if (openedCount === 0) {
        showToast(
          "Pop-up blocked. Allow pop-ups to open attachments in new tabs.",
          "error",
        );
        return;
      }
      if (openedCount < paths.length) {
        showToast(
          "Some pop-ups were blocked. Allow pop-ups to open every attachment.",
          "error",
        );
      }

      for (let index = 0; index < paths.length; index += 1) {
        const tab = tabs[index];
        if (!tab) {
          continue;
        }

        try {
          const url = await getPreviewTargetUrl(paths[index]);
          if (url.startsWith("blob:")) {
            setTimeout(() => URL.revokeObjectURL(url), 60000);
          }
          if (!tab.closed) {
            tab.location.replace(url);
            tab.opener = null;
          }
        } catch (err) {
          console.error("Open attachment failed", err);
          if (!tab.closed) {
            tab.close();
          }
          showToast(err?.message || "Failed to open attachment", "error");
        }
      }
    },
    [getPreviewTargetUrl, showToast],
  );

  const handleReindex = useCallback(
    async (msgId) => {
      try {
        await callApi(
          instance,
          accounts,
          "PUT",
          `/api/metadata/reindex/${msgId}`,
        );
        showToast("Reindex triggered successfully", "success");
        const result = await loadVerifiedThreads();
        if (!openThread || !result) {
          return;
        }

        if (result.grouped) {
          const refreshed = await Promise.all(
            [...expandedGroups].map((type) =>
              loadDocumentTypeGroup(type, groupPages[type] || 1),
            ),
          );
          const nextThread = refreshed
            .flatMap((item) => item?.data || [])
            .find(
              (item) => item.conversation_id === openThread.conversation_id,
            );
          setOpenThread(nextThread || null);
          return;
        }

        if (result.data) {
          const nextThread = result.data.find(
            (item) => item.conversation_id === openThread.conversation_id,
          );
          setOpenThread(nextThread || null);
        }
      } catch (err) {
        console.error("Reindex failed", err);
        showToast("Failed to reindex metadata", "error");
      }
    },
    [
      accounts,
      expandedGroups,
      groupPages,
      instance,
      loadDocumentTypeGroup,
      loadVerifiedThreads,
      openThread,
      showToast,
    ],
  );

  const handleDownload = useCallback(
    async (path, fileName) => {
      try {
        const cleaned = path?.trim();
        if (!cleaned) return;

        const { filesystem, path: filePath } = resolveAdlsPath(
          cleaned,
          FILESYSTEM,
        );
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
        const BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_CLIENT_ID;
        const token = await instance.acquireTokenSilent({
          account: instance.getActiveAccount() || accounts?.[0],
          scopes: [`api://${BACKEND_CLIENT_ID}/access_as_user`],
        });
        const response = await fetch(
          `${baseUrl}/api/adls/download` +
            `?filesystem=${encodeURIComponent(filesystem)}` +
            `&path=${encodeURIComponent(filePath)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Download failed");
        }

        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          throw new Error("Empty download response");
        }

        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName || filePath.split("/").pop() || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error("Download failed", err);
        showToast(err?.message || "Failed to download file", "error");
      }
    },
    [FILESYSTEM, accounts, instance, showToast],
  );

  const handleSaved = async () => {
    const conversationId =
      selectedRow?.conversation_id || openThread?.conversation_id;
    const savedMsgId = selectedRow?.msg_id || selectedMsgId;

    showToast("Metadata verified successfully");

    const result = await loadPendingThreads();
    if (!result) {
      closeReview();
      return;
    }

    if (result.grouped) {
      const refreshed = await Promise.all(
        [...expandedGroups].map((type) =>
          loadDocumentTypeGroup(type, groupPages[type] || 1),
        ),
      );
      const thread = refreshed
        .flatMap((item) => item?.data || [])
        .find((item) => item.conversation_id === conversationId);
      const nextEmail = nextPendingEmail(thread, savedMsgId);

      if (!thread) {
        closeThreadView();
        return;
      }

      setOpenThread(thread);
      closeReview();

      if (nextEmail) {
        setSelectedMsgId(nextEmail.msg_id);
        return;
      }

      if (!isChain(thread)) {
        closeThreadView();
      }
      return;
    }

    if (result.data.length === 0 && page > 1 && result.total > 0) {
      setPage((current) => Math.max(1, current - 1));
      closeThreadView();
      return;
    }

    const thread = result.data.find(
      (item) => item.conversation_id === conversationId,
    );
    const nextEmail = nextPendingEmail(thread, savedMsgId);

    if (!thread) {
      closeThreadView();
      return;
    }

    setOpenThread(thread);
    closeReview();

    if (nextEmail) {
      setSelectedMsgId(nextEmail.msg_id);
      return;
    }

    if (!isChain(thread)) {
      closeThreadView();
    }
  };

  const isPending = activeTab === "pending";
  const data = isPending ? threads : verifiedThreads;
  const isLoading = isPending ? loading : verifiedLoading;
  const currentPage = isPending ? page : verifiedPage;
  const currentTotal = isPending ? total : verifiedTotal;
  const currentTotalPages = Math.ceil(currentTotal / PAGE_SIZE) || 1;
  const totalEmails = isPending ? recordTotal : verifiedRecordTotal;
  const individualCount = isPending ? individualTotal : verifiedIndividualTotal;
  const groupedCount = isPending ? groupedTotal : verifiedGroupedTotal;
  const groupByDocumentType = shouldGroupWorkPoolByDocumentType(
    appliedDocumentTypes,
  );
  const threadGroups = groupByDocumentType ? typeCounts : null;

  const renderGroupHeader = (type, count, { mobile = false } = {}) => {
    const countLabel = `${formatQueueCount(count)} ${
      count === 1 ? "record" : "records"
    }`;
    const expanded = expandedGroups.has(type);
    const label = `${type} group, ${countLabel}`;
    const toggle = (
      <button
        type="button"
        style={mobile ? styles.mobileGroupToggle : styles.groupToggle}
        aria-expanded={expanded}
        aria-label={label}
        onClick={() => toggleDocumentTypeGroup(type)}
      >
        {expanded ? (
          <ChevronDown size={14} color="#64748b" aria-hidden="true" />
        ) : (
          <ChevronRight size={14} color="#64748b" aria-hidden="true" />
        )}
        <span style={styles.groupType}>{type}</span>
        <span style={styles.groupCount}>{countLabel}</span>
      </button>
    );

    if (mobile) {
      return (
        <div key={`${type}-header`} style={styles.mobileGroupHeader}>
          {toggle}
        </div>
      );
    }

    return (
      <tr key={`${type}-header`}>
        <td colSpan="6" style={styles.groupHeaderCell}>
          {toggle}
        </td>
      </tr>
    );
  };

  const renderMetadata = (email) => {
    if (!email) {
      return "—";
    }

    return (
      <div style={styles.metaBlock}>
        <div style={styles.metaTitleRow}>
          <span style={styles.metaTitle}>
            {email.document_type || "UNKNOWN"}
          </span>
          <span style={styles.policyChip}>
            <FileText size={12} />
            <span style={styles.policyChipText}>
              {email.policy_number || "N/A"}
            </span>
          </span>
        </div>
        <div style={styles.metaSub}>
          <span style={styles.metaIcon} aria-hidden="true">
            <Clock3 size={13} />
          </span>
          <span style={styles.metaEntryText}>
            Entry: {formatDate(email.created_at)}
          </span>
        </div>
      </div>
    );
  };

  const renderWorkItemId = (thread) => {
    const witsSerial = thread.wits_serial_number || (thread.emails?.[0]?.wits_serial_number) || "—";

    return (
      <div style={styles.workItemIdCell}>
        <span
          style={styles.workItemBadge}
          title={`Work Item ID: ${witsSerial}`}
        >
          {witsSerial}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setHistoryModalItem({ ...thread, isWorkPool: true });
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "3px",
            padding: "2px 6px",
            marginTop: "4px",
            borderRadius: "5px",
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#2563eb",
            fontSize: "11px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
          }}
          title="View Work Pool History Timeline"
        >
          <History size={11} color="#2563eb" />
          <span>History</span>
        </button>
      </div>
    );
  };

  const renderSource = (thread) => {
    const chained = isChain(thread);
    const summary = threadSummary(thread);

    return (
      <div style={styles.threadCell}>
        <span
          style={{
            ...styles.sourceBadge,
            ...(chained ? styles.sourceBadgeThread : styles.sourceBadgeSingle),
          }}
        >
          {chained ? <Inbox size={12} /> : <Mail size={12} />}
          {summary.title}
        </span>
        <div style={styles.sourceDetail}>{summary.detail}</div>
        <button
          type="button"
          style={styles.sourceViewBtn}
          onClick={() => openThreadView(thread)}
        >
          {summary.link}
          <ChevronRight size={12} />
        </button>
      </div>
    );
  };

  const renderStatusCell = (thread) => {
    const status = threadStatus(thread);
    const singleton = thread.emails?.[0];
    const metaEmail = isChain(thread) ? latestThreadEmail(thread) : singleton;
    const assignedTo = thread.ado_assigned_to || metaEmail?.ado_assigned_to || thread.assigned_to || null;
    const storyId = thread.ado_user_story_id || metaEmail?.ado_user_story_id || thread.user_story_id || null;
    const org = thread.ado_organization || metaEmail?.ado_organization || "DWS10062665";
    const project = thread.ado_project || metaEmail?.ado_project || "ADLS Migration";

    const displayName = formatAssignedTo(assignedTo);
    const hasAssignee = displayName && displayName !== "Unassigned";

    const linkUrl = storyId
      ? `https://dev.azure.com/${encodeURIComponent(org)}/${encodeURIComponent(project)}/_workitems/edit/${storyId}`
      : null;

    return (
      <div style={styles.threadCell}>
        <div>
          <StatusChip tone={status.tone} label={status.label} />
        </div>
        {hasAssignee && (
          <div
            style={{
              fontSize: "11.5px",
              color: "#475569",
              fontWeight: "600",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              maxWidth: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            title={`Assigned to: ${assignedTo}`}
          >
            <User size={11} color="#64748b" style={{ flexShrink: 0 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </span>
          </div>
        )}
        {storyId && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "11.5px",
              color: "#2563eb",
              fontWeight: "700",
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              textDecoration: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            title={`Open User Story ${storyId} in Azure DevOps`}
            onClick={(e) => e.stopPropagation()}
          >
            <span>User Story {storyId}</span>
            <ExternalLink size={10} style={{ flexShrink: 0 }} />
          </a>
        )}
      </div>
    );
  };

  const renderParentRow = (thread, index) => {
    const chained = isChain(thread);
    const singleton = thread.emails?.[0];
    const metaEmail = chained ? latestThreadEmail(thread) : singleton;
    const opened = openThread?.conversation_id === thread.conversation_id;

    return (
      <tr
        key={thread.conversation_id}
        style={{
          ...styles.row,
          ...(index % 2 === 1 ? styles.rowAlt : {}),
          ...(opened ? styles.rowOpened : {}),
        }}
      >
        <td
          style={{
            ...styles.td,
            ...styles.tdAccent,
            ...rowAccent(chained, opened),
          }}
        >
          {renderWorkItemId(thread)}
        </td>
        <td style={styles.td}>
          {renderSource(thread)}
        </td>
        <td style={styles.td}>{renderMetadata(metaEmail)}</td>
        <td style={styles.td}>
          <div style={styles.subject}>
            <span style={styles.subjectIcon}>
              <Mail size={14} color="#64748b" />
            </span>
            <TruncatedWithTooltip
              text={thread.latest_email_subject || "—"}
              style={styles.subjectText}
            />
          </div>
        </td>
        <td style={styles.tdSender}>
          <TruncatedWithTooltip
            text={thread.latest_mail_from_address || "—"}
            style={styles.senderText}
          />
        </td>
        <td style={styles.tdStatus}>
          {renderStatusCell(thread)}
        </td>
      </tr>
    );
  };

  const renderMobileThread = (thread) => {
    const chained = isChain(thread);
    const opened = openThread?.conversation_id === thread.conversation_id;
    const witsSerial = thread.wits_serial_number || (thread.emails?.[0]?.wits_serial_number) || "—";
    return (
      <div
        key={thread.conversation_id}
        style={{
          ...styles.mobileThread,
          ...rowAccent(chained, opened),
        }}
      >
        <div style={styles.mobileParent}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <span
              style={styles.workItemBadge}
              title={`Work Item ID: ${witsSerial}`}
            >
              {witsSerial}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setHistoryModalItem({ ...thread, isWorkPool: true });
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "3px 8px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#2563eb",
                fontSize: "11.5px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
              }}
              title="View History Timeline"
            >
              <History size={12} color="#2563eb" />
              <span>History</span>
            </button>
          </div>
          {renderSource(thread)}
          <div style={{ marginTop: 10 }}>
            {renderMetadata(latestThreadEmail(thread) || thread.emails?.[0])}
          </div>
          <div style={{ marginTop: 8 }}>
            <TruncatedWithTooltip
              text={thread.latest_email_subject || "—"}
              style={styles.subjectText}
            />
          </div>
          <div style={styles.metaSub}>
            {thread.latest_mail_from_address || "—"}
          </div>
          <div style={{ marginTop: 8 }}>
            {renderStatusCell(thread)}
          </div>
        </div>
      </div>
    );
  };

  const renderGroupLoading = ({ mobile = false } = {}) => {
    if (mobile) {
      return <div style={styles.empty}>Loading metadata...</div>;
    }

    return (
      <tr>
        <td colSpan="6" style={styles.empty}>
          Loading metadata...
        </td>
      </tr>
    );
  };

  const renderGroupPagination = (type, group, { mobile = false } = {}) => {
    if (!group || group.loading || !(group.total > 0)) {
      return null;
    }

    const pager = (
      <Pagination
        compact
        ariaLabel={`${type} pagination`}
        page={groupPages[type] || 1}
        totalPages={Math.ceil(group.total / PAGE_SIZE) || 1}
        totalRecords={group.total}
        pageSize={PAGE_SIZE}
        currentPageCount={(group.data || []).length}
        onPageChange={(nextPage) => loadDocumentTypeGroup(type, nextPage)}
      />
    );

    if (mobile) {
      return <div style={styles.groupPager}>{pager}</div>;
    }

    return (
      <tr>
        <td colSpan="6" style={styles.groupPagerCell}>
          {pager}
        </td>
      </tr>
    );
  };

  const renderGroupedMobileThreads = () =>
    threadGroups.map(({ document_type: type, total }) => {
      const expanded = expandedGroups.has(type);
      const group = groupResults[type];
      const rows = group?.data || [];
      const loadingGroup = Boolean(
        expanded && group?.loading && rows.length === 0,
      );
      return (
        <div key={type}>
          {renderGroupHeader(type, total, { mobile: true })}
          {expanded ? (
            loadingGroup ? (
              renderGroupLoading({ mobile: true })
            ) : (
              <>
                {rows.map(renderMobileThread)}
                {renderGroupPagination(type, group, { mobile: true })}
              </>
            )
          ) : null}
        </div>
      );
    });

  const renderGroupedTableRows = () =>
    threadGroups.map(({ document_type: type, total }) => {
      const expanded = expandedGroups.has(type);
      const group = groupResults[type];
      const rows = group?.data || [];
      const loadingGroup = Boolean(
        expanded && group?.loading && rows.length === 0,
      );
      return (
        <React.Fragment key={type}>
          {renderGroupHeader(type, total)}
          {expanded ? (
            loadingGroup ? (
              renderGroupLoading()
            ) : (
              <>
                {rows.map((thread, index) => renderParentRow(thread, index))}
                {renderGroupPagination(type, group)}
              </>
            )
          ) : null}
        </React.Fragment>
      );
    });

  const showEmpty =
    !isLoading &&
    (groupByDocumentType
      ? currentTotal === 0 &&
        typeCounts.every((group) => group.total === 0)
      : data.length === 0);

  const renderQueue = () => (
    <>
    <div style={styles.card} className="rtc-card-shell--loading-host">
      <LoadingOverlay show={isLoading} label="Loading metadata..." />
      {isNarrow ? (
        <div>
          {showEmpty ? (
            <div style={styles.empty}>
              <div style={styles.emptyTitle}>
                No Work Items Found
              </div>
            </div>
          ) : threadGroups ? (
            renderGroupedMobileThreads()
          ) : (
            data.map(renderMobileThread)
          )}
        </div>
      ) : (
        <ScrollableTable
          style={{
            ...styles.tableWrapper,
            ...(isLoading && data.length === 0 ? { minHeight: 180 } : {}),
            ...(isLoading ? { opacity: 0.45 } : {}),
          }}
        >
          <table style={styles.table}>
            <colgroup>
              <col style={styles.colWorkItemId} />
              <col style={styles.colSource} />
              <col style={styles.colMetadata} />
              <col style={styles.colSubject} />
              <col style={styles.colSender} />
              <col style={styles.colStatus} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>Work Item ID</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Metadata</th>
                <th style={styles.th}>Email Subject</th>
                <th style={styles.th}>Sender</th>
                <th style={styles.thStatus}>Status</th>
              </tr>
            </thead>
            <tbody>
              {showEmpty ? (
                <tr>
                  <td colSpan="6" style={styles.empty}>
                    <div style={styles.emptyState}>
                      <Files size={42} strokeWidth={1.5} />
                      <div style={styles.emptyTitle}>
                        No Work Items Found
                      </div>
                      <div style={styles.emptySub}>
                        No records match the current criteria.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : threadGroups ? (
                renderGroupedTableRows()
              ) : (
                data.map((thread, index) => renderParentRow(thread, index))
              )}
            </tbody>
          </table>
        </ScrollableTable>
      )}
    </div>

    {!isLoading && !groupByDocumentType && currentTotal > 0 && (
      <Pagination
        page={currentPage}
        totalPages={currentTotalPages}
        totalRecords={currentTotal}
        pageSize={PAGE_SIZE}
        currentPageCount={data.length}
        onPageChange={(nextPage) =>
          isPending ? setPage(nextPage) : setVerifiedPage(nextPage)
        }
      />
    )}
    </>
  );

  if (historyModalItem) {
    return (
      <div
        style={{
          ...styles.container,
          ...(isSmallScreen ? styles.containerNarrow : {}),
        }}
      >
        <RequestHistoryModal
          isOpen={true}
          onClose={() => setHistoryModalItem(null)}
          workItem={historyModalItem}
          isWorkPool={true}
          isFullPage={true}
        />
        {toast && (
          <div
            style={{
              ...styles.toast,
              background: toast.type === "success" ? "#16a34a" : "#dc2626",
            }}
          >
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.container,
        ...(isSmallScreen ? styles.containerNarrow : {}),
        ...(reviewMode && !openThread ? styles.reviewContainerMode : {}),
      }}
    >
      {(!reviewMode || openThread) && (
        <>
          {!openThread && (
          <div
            style={{
              ...styles.header,
              ...(isSmallScreen ? styles.headerNarrow : {}),
            }}
          >
            <div>
              <h2 style={styles.title}>
                <Clock3 size={20} color="#64748b" />
                Work Pool Queue
              </h2>
              <p style={styles.sub}>
                Review, validate, and update document metadata across all records.
              </p>
            </div>
            <div
              style={{
                ...styles.summaryRow,
                ...(isSmallScreen ? styles.summaryRowNarrow : {}),
              }}
            >
              <div
                style={{
                  ...styles.statsCard,
                  ...styles.statsCardNeutral,
                  ...(isSmallScreen ? styles.statsCardNarrow : {}),
                }}
              >
                <div
                  style={{
                    ...styles.statsValue,
                    ...(isSmallScreen ? styles.statsValueNarrow : {}),
                  }}
                >
                  {formatQueueCount(totalEmails)}
                </div>
                <div
                  style={{
                    ...styles.statsLabel,
                    ...(isSmallScreen ? styles.statsLabelNarrow : {}),
                  }}
                >
                  Total records
                </div>
              </div>
              <div
                style={{
                  ...styles.statsCard,
                  ...styles.statsCardOrange,
                  ...(isSmallScreen ? styles.statsCardNarrow : {}),
                }}
              >
                <div
                  style={{
                    ...styles.statsValue,
                    ...(isSmallScreen ? styles.statsValueNarrow : {}),
                  }}
                >
                  {formatQueueCount(individualCount)}
                </div>
                <div
                  style={{
                    ...styles.statsLabel,
                    ...(isSmallScreen ? styles.statsLabelNarrow : {}),
                  }}
                >
                  Individuals awaiting review
                </div>
              </div>
              <div
                style={{
                  ...styles.statsCard,
                  ...styles.statsCardBlue,
                  ...(isSmallScreen ? styles.statsCardNarrow : {}),
                }}
              >
                <div
                  style={{
                    ...styles.statsValue,
                    ...(isSmallScreen ? styles.statsValueNarrow : {}),
                  }}
                >
                  {formatQueueCount(groupedCount)}
                </div>
                <div
                  style={{
                    ...styles.statsLabel,
                    ...(isSmallScreen ? styles.statsLabelNarrow : {}),
                  }}
                >
                  {isPending ? "Threads in progress" : "Threads"}
                </div>
              </div>
            </div>
          </div>
          )}

          {!openThread && (
            <>
          <div style={styles.filterCard}>
            <div style={styles.filterGrid}>
              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Search</label>
                <input
                  type="text"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  placeholder="Search by Email or Work Item ID"
                  style={styles.filterInput}
                />
              </div>

              <div style={styles.filterField}>
                <label style={styles.filterLabel}>Document Type</label>
                <DocumentTypeMultiSelect
                  options={documentTypeOptions}
                  value={documentTypes}
                  onChange={setDocumentTypes}
                  placeholder="Select document type"
                />
              </div>

              <div style={styles.filterField}>
                <label style={styles.filterLabel}>From Date & Time</label>
                <input
                  type="datetime-local"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={styles.filterInput}
                />
              </div>

              <div style={styles.filterField}>
                <label style={styles.filterLabel}>To Date & Time</label>
                <input
                  type="datetime-local"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={styles.filterInput}
                />
              </div>

              <div style={styles.filterActions}>
                <button style={styles.searchBtn} onClick={handleSearch}>
                  Search
                </button>
                <button style={styles.clearBtn} onClick={handleClearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
            </>
          )}

          <div ref={queueTopRef}>
            {openThread ? (
              <InboxReviewThreadView
                key={openThread.conversation_id}
                thread={openThread}
                activeTab={activeTab}
                selectedMsgId={selectedMsgId}
                selectedRow={selectedRow}
                reviewMode={reviewMode}
                isNarrow={isNarrow}
                previewLoading={previewLoading}
                previewUrl={inlinePreviewUrl}
                previewKind={inlinePreviewKind}
                previewError={inlinePreviewError}
                onSelectEmail={selectThreadEmail}
                onCollapse={closeThreadView}
                onEdit={(email) =>
                  openReview(email, openThread.conversation_id)
                }
                onReindex={handleReindex}
                onPreview={handlePreview}
                onOpenAll={handleOpenAllAttachments}
                onDownload={handleDownload}
                onCloseReview={closeReview}
                onSaved={handleSaved}
              />
            ) : (
              renderQueue()
            )}
          </div>
        </>
      )}

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#dc2626",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "24px",
    background: "#f8fafc",
    minHeight: "80vh",
  },
  containerNarrow: {
    padding: "12px 12px 28px",
  },
  reviewContainerMode: {
    height: "100vh",
    overflow: "hidden",
    padding: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    gap: "20px",
  },
  headerNarrow: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "12px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "700",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },
  summaryRow: {
    display: "flex",
    gap: "8px",
    flexShrink: 0,
    alignItems: "stretch",
  },
  summaryRowNarrow: {
    width: "100%",
    flexDirection: "column",
  },
  tabs: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
  },
  tabsNarrow: {
    flexWrap: "wrap",
    marginBottom: "20px",
    width: "100%",
  },
  tabBtn: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#475569",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
  tabBtnNarrow: {
    flex: "1 1 48%",
    minWidth: 0,
    textAlign: "center",
  },
  activeTabBtn: {
    background: "#2563eb",
    color: "#ffffff",
    border: "1px solid #2563eb",
  },
  statsCard: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: "12px",
    padding: "8px 12px",
    minWidth: "128px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  statsCardNarrow: {
    minWidth: "100%",
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
  },
  statsCardNeutral: {
    borderColor: "#dbeafe",
  },
  statsCardOrange: {
    borderColor: "#fdba74",
    background: "#fffbf7",
  },
  statsCardBlue: {
    borderColor: "#93c5fd",
    background: "#f8fbff",
  },
  statsValue: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#2563eb",
  },
  statsValueOrange: {
    color: "#ea580c",
  },
  statsValueBlue: {
    color: "#2563eb",
  },
  statsValueNarrow: {
    fontSize: "18px",
  },
  statsLabel: {
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
  },
  statsLabelNarrow: {
    fontSize: "11px",
    marginTop: "2px",
  },
  filterCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "14px",
    overflow: "visible",
  },
  filterGrid: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 180px",
    minWidth: 0,
  },
  filterFieldWide: {
    flex: "1 1 240px",
  },
  filterLabel: {
    fontSize: "11px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  filterInput: {
    height: "34px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "0 10px",
    fontSize: "12px",
    outline: "none",
    background: "#fff",
  },
  filterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
    flex: "0 0 auto",
    marginLeft: "auto",
  },
  searchBtn: {
    height: "34px",
    padding: "0 14px",
    border: "none",
    borderRadius: "8px",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "12px",
  },
  clearBtn: {
    height: "34px",
    padding: "0 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "12px",
  },
  card: {
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #e2e8f0",
  },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: "600",
    color: "#0f172a",
  },
  tableWrapper: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    width: "100%",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  colWorkItemId: { width: "13%" },
  colSource: { width: "13%" },
  colMetadata: { width: "23%" },
  colSubject: { width: "21%" },
  colSender: { width: "15%" },
  colStatus: { width: "15%" },
  workItemIdCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  workItemBadge: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "12px",
    fontWeight: "700",
    color: "#0f172a",
    background: "#f1f5f9",
    padding: "3px 7px",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
    display: "inline-block",
    whiteSpace: "nowrap",
    letterSpacing: "0.2px",
    userSelect: "text",
    cursor: "text",
  },
  adoLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "11px",
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: "600",
    background: "#eff6ff",
    padding: "1px 5px",
    borderRadius: "4px",
    border: "1px solid #bfdbfe",
    transition: "all 0.15s ease",
  },
  adoTag: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
    background: "#f8fafc",
    padding: "1px 5px",
    borderRadius: "4px",
    border: "1px solid #e2e8f0",
  },
  th: {
    textAlign: "left",
    whiteSpace: "nowrap",
    background: "#f8fafc",
    padding: "10px 14px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
  },
  thStatus: {
    textAlign: "left",
    background: "#f8fafc",
    padding: "10px 14px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  thRight: {
    textAlign: "right",
    background: "#f8fafc",
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  row: {},
  rowAlt: {
    background: "#f8fbff",
  },
  rowOpened: {
    background: "#eff6ff",
  },
  accentSingleLite: {
    borderLeft: "2px solid #fdba74",
  },
  accentSingleDark: {
    borderLeft: "3px solid #f97316",
  },
  accentThreadLite: {
    borderLeft: "2px solid #93c5fd",
  },
  accentThreadDark: {
    borderLeft: "3px solid #2563eb",
  },
  tdAccent: {
    overflow: "visible",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    fontSize: "13px",
    overflow: "hidden",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    textAlign: "left",
  },
  tdSender: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    fontSize: "13px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
    color: "#475569",
  },
  senderText: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#475569",
    fontSize: "13px",
  },
  tdStatus: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    textAlign: "left",
    overflow: "visible",
  },
  statusCell: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  tdRight: {
    padding: "14px 12px",
    borderBottom: "1px solid #f1f5f9",
    textAlign: "right",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  threadCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  chevronBtn: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    flexShrink: 0,
    padding: 0,
  },
  sourceBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "2px 7px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  sourceBadgeSingle: {
    background: "#fff7ed",
    color: "#c2410c",
    borderColor: "#fdba74",
  },
  sourceBadgeThread: {
    background: "#eff6ff",
    color: "#1d4ed8",
    borderColor: "#93c5fd",
  },
  sourceDetail: {
    color: "#64748b",
    fontSize: 12,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  sourceViewBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    margin: 0,
    padding: 0,
    border: 0,
    background: "transparent",
    color: "#2563eb",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  moreBtn: {
    width: 32,
    height: 32,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#64748b",
    cursor: "pointer",
    borderRadius: 7,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  chainBtn: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: 8,
    border: 0,
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    color: "inherit",
    textAlign: "left",
  },
  chainText: {
    fontWeight: "600",
    color: "#0f172a",
  },
  metaBlock: {
    minWidth: 0,
  },
  metaTitleRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: 8,
    rowGap: 6,
    minWidth: 0,
  },
  metaTitle: {
    fontWeight: 700,
    color: "#0f172a",
    fontSize: 12,
    wordBreak: "break-word",
    minWidth: 0,
  },
  policyChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: "100%",
    padding: "2px 7px",
    borderRadius: 999,
    background: "#f5f3ff",
    color: "#6d28d9",
    border: "1px solid #c4b5fd",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.2,
    flex: "0 1 auto",
    minWidth: 0,
  },
  policyChipText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaSub: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    color: "#64748b",
    fontSize: 13,
    marginTop: 6,
  },
  metaIcon: {
    display: "flex",
    flexShrink: 0,
    marginTop: 2,
  },
  metaEntryText: {
    minWidth: 0,
    fontSize: 12,
    lineHeight: 1.4,
  },
  subject: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    minWidth: 0,
  },
  subjectIcon: {
    display: "flex",
    flexShrink: 0,
    marginTop: 2,
  },
  subjectText: {
    fontWeight: 400,
    color: "#475569",
    minWidth: 0,
    flex: 1,
    lineHeight: 1.4,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  },
  subjectTooltip: {
    position: "fixed",
    transform: "translateY(calc(-100% - 10px))",
    background: "#0f172a",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.4,
    maxWidth: 360,
    zIndex: 9999,
    pointerEvents: "none",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.28)",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  subjectTooltipArrow: {
    position: "absolute",
    left: 12,
    bottom: -4,
    width: 8,
    height: 8,
    background: "#0f172a",
    transform: "rotate(45deg)",
    borderRadius: 1,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid",
    whiteSpace: "nowrap",
  },
  chipNew: {
    background: "#eab308",
    color: "#ffffff",
    borderColor: "#ca8a04",
    fontWeight: 700,
  },
  chipReview: {
    background: "#f97316",
    color: "#ffffff",
    borderColor: "#ea580c",
    fontWeight: 700,
  },
  chipProgress: {
    background: "#2563eb",
    color: "#ffffff",
    borderColor: "#1d4ed8",
    fontWeight: 700,
  },
  chipVerified: {
    background: "#16a34a",
    color: "#ffffff",
    borderColor: "#15803d",
    fontWeight: 700,
  },
  chipDot: {
    width: 6,
    height: 6,
    borderRadius: 999,
    flexShrink: 0,
  },
  chipDotNew: {
    background: "#ffffff",
  },
  chipDotReview: {
    background: "#ffffff",
  },
  chipDotProgress: {
    background: "#ffffff",
  },
  chipDotVerified: {
    background: "#ffffff",
  },
  locked: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 600,
  },
  linkBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: 0,
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    fontSize: 12,
    padding: 0,
  },
  fileList: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "#334155",
  },
  previewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: 6,
    border: "none",
    background: "#e0e7ff",
    color: "#2563eb",
    cursor: "pointer",
  },
  updateBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0f172a",
    color: "#ffffff",
    border: "none",
    width: 32,
    height: 32,
    borderRadius: 7,
    cursor: "pointer",
    flexShrink: 0,
    boxShadow: "0 1px 2px rgba(15,23,42,0.15)",
  },
  empty: {
    padding: "60px 20px",
    textAlign: "center",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    color: "#94a3b8",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#334155",
  },
  emptySub: {
    fontSize: "14px",
    color: "#64748b",
  },
  groupHeaderCell: {
    padding: 0,
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    textAlign: "left",
  },
  groupToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    margin: 0,
    padding: "8px 18px",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },
  groupType: {
    fontSize: 12,
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "0.02em",
  },
  groupCount: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 600,
  },
  groupPagerCell: {
    padding: "8px 18px 12px",
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
  },
  groupPager: {
    padding: "8px 18px 12px",
    background: "#fff",
    borderTop: "1px solid #e2e8f0",
  },
  mobileGroupHeader: {
    padding: 0,
    background: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
  },
  mobileGroupToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    margin: 0,
    padding: "10px 18px",
    border: 0,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },
  toast: {
    position: "fixed",
    right: "24px",
    bottom: "24px",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: "10px",
    zIndex: 50,
  },
  mobileThread: {
    borderTop: "1px solid #e2e8f0",
  },
  mobileParent: {
    width: "100%",
    textAlign: "left",
    border: 0,
    background: "#fff",
    padding: "14px 18px",
    cursor: "pointer",
  },
  mobileParentTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  mobileChild: {
    padding: "12px 18px 16px 28px",
    background: "#f8fafc",
    borderTop: "1px solid #e2e8f0",
  },
  reviewWorkspace: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflow: "hidden",
  },
  reviewContent: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    minHeight: 0,
  },
  reviewContentNarrow: {
    flexDirection: "column",
  },
  reviewMain: {
    flex: 1,
    minWidth: 0,
    padding: "1rem",
    overflow: "auto",
  },
  workspaceLayout: {
    width: "480px",
    minWidth: "480px",
    maxWidth: "480px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: "1rem",
  },
  workspaceLayoutNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    margin: 0,
  },
};
